/* eslint-disable react/no-array-index-key */
import {css, SerializedStyles} from '@emotion/react';
import {useTheme} from '@mui/material';
import {ArrayUtils, DlMuiDynamicIcon, GetStylesOptions, HierarchyNode} from '@wallaby-ui/core';
import React, {HTMLAttributes, JSXElementConstructor, useCallback, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';

import {getSelectOptions, LightSelectFilter, LightSelectFilterProps, NormalizedOption, SelectParams} from './LightSelectFilter';

export type HierarchyFilterProps<T = any> = Omit<LightSelectFilterProps<T>, 'PopperComponent' | 'open'> & {
  getNodeIcon?: (args: {
    node: HierarchyNode<NormalizedOption<T>>;
    hasChildren: boolean;
    expanded: boolean;
    disabled: boolean;
    selected: boolean;
    toggle: () => void;
  }) => React.ReactNode;
  getNodeSeparator?: (args: {
    node: HierarchyNode<NormalizedOption<T>>;
    hasChildren: boolean;
    expanded: boolean;
    disabled: boolean;
    selected: boolean;
  }) => React.ReactNode;
  hierarchyNodeCss?: (selected: HierarchyNode<NormalizedOption<T>>) => SerializedStyles;
  ignoreOption?: (option: NormalizedOption<T>) => boolean | undefined;
  defaultIsExpanded?: boolean;
  renderNodeLabel?: (node: HierarchyNode<NormalizedOption<T>>) => React.ReactNode;
};

// hierarchy listbox
type HierarchyListboxProps = {
  styles: any;
  nodes: HierarchyNode<NormalizedOption>[];
  valueIdArray: string[];
  hierarchyLabelOnClick: (node: HierarchyNode<NormalizedOption>, event: React.MouseEvent) => void;
  expandedNodes: Record<string | number, boolean | undefined>;
  setExpandedNodes: (newValue: Record<string | number, boolean | undefined>) => void;
  ignoreOption: ((option: NormalizedOption) => boolean | undefined) | undefined;
  defaultIsExpanded: boolean;
  getNodeIcon?: HierarchyFilterProps['getNodeIcon'];
  getNodeSeparator?: HierarchyFilterProps['getNodeSeparator'];
  renderNodeLabel?: HierarchyFilterProps['renderNodeLabel'];
};
const HierarchyListbox = React.forwardRef<HTMLDivElement, React.PropsWithChildren<HierarchyListboxProps>>(function HierarchyListbox(
  props,
  ref
) {
  const {
    styles,
    nodes,
    valueIdArray,
    hierarchyLabelOnClick,
    expandedNodes,
    setExpandedNodes,
    ignoreOption,
    defaultIsExpanded,
    getNodeIcon,
    getNodeSeparator,
    renderNodeLabel,
    children: _,
    ...extraProps
  } = props;
  // NOTE: we need to include props.onMouseDown so that we don't get closed on mouse down
  return (
    <div {...extraProps} ref={ref} css={styles.hierarchyListbox}>
      {nodes.map((node, i) => (
        <LightHierarchyNode
          key={i}
          valueIdArray={valueIdArray}
          node={node}
          styles={styles}
          hierarchyLabelOnClick={hierarchyLabelOnClick}
          expandedNodes={expandedNodes}
          setExpandedNodes={setExpandedNodes}
          ignoreOption={ignoreOption}
          defaultIsExpanded={defaultIsExpanded}
          getNodeIcon={getNodeIcon}
          getNodeSeparator={getNodeSeparator}
          renderNodeLabel={renderNodeLabel}
        />
      ))}
    </div>
  );
});

// hierarchy node
type LightHierarchyNodeProps = {
  valueIdArray: string[];
  node: HierarchyNode<NormalizedOption>;
  styles: any;
  hierarchyLabelOnClick: (node: HierarchyNode<NormalizedOption>, event: React.MouseEvent) => void;
  expandedNodes: Record<string | number, boolean | undefined>;
  setExpandedNodes: (newValue: Record<string | number, boolean | undefined>) => void;
  ignoreOption: ((option: NormalizedOption) => boolean | undefined) | undefined;
  defaultIsExpanded: boolean;
  getNodeIcon?: HierarchyFilterProps['getNodeIcon'];
  getNodeSeparator?: HierarchyFilterProps['getNodeSeparator'];
  renderNodeLabel?: HierarchyFilterProps['renderNodeLabel'];
};
const LightHierarchyNode: React.FC<LightHierarchyNodeProps> = (props) => {
  const {
    valueIdArray,
    node,
    styles,
    hierarchyLabelOnClick,
    expandedNodes,
    setExpandedNodes,
    ignoreOption,
    defaultIsExpanded,
    getNodeIcon,
    getNodeSeparator,
    renderNodeLabel,
  } = props;
  const hasChildren = Boolean(node.children?.length);
  const isExpanded = Boolean(expandedNodes[node.id as string] ?? (defaultIsExpanded && hasChildren));
  const disabled = Boolean(node.disabled);
  const selected = valueIdArray.includes(node.id as string);

  const toggle = useCallback(() => {
    if (!hasChildren) return;
    setExpandedNodes({...expandedNodes, [node.id as string]: !isExpanded});
  }, [hasChildren, expandedNodes, node.id, isExpanded, setExpandedNodes]);

  const defaultIcon = (
    <DlMuiDynamicIcon
      className='hierarchy-expand-button'
      iconName={hasChildren ? 'hierarchyArrowIcon' : 'hierarchyFileIcon'}
      useFixedViewBox={16}
      onClick={hasChildren ? toggle : undefined}
      noButtonStyles
    />
  );

  const renderIcon = () => {
    const custom = getNodeIcon?.({node, hasChildren, expanded: isExpanded, disabled, selected, toggle});
    if (!custom) return defaultIcon;

    if (hasChildren) {
      return (
        <button
          css={{border: 0, margin: 0, background: 'none', backgroundColor: 'transparent'}}
          type='button'
          className='hierarchy-expand-button'
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
          aria-expanded={isExpanded}
          onClick={toggle}
          tabIndex={0}
        >
          {custom}
        </button>
      );
    }
    return <span className='hierarchy-expand-button'>{custom}</span>;
  };

  if (ignoreOption && ignoreOption(node)) {
    return (
      <div className='hierarchy-children'>
        {node.children?.map((node, i) => (
          <LightHierarchyNode
            key={i}
            valueIdArray={valueIdArray}
            getNodeIcon={getNodeIcon}
            node={node}
            styles={styles}
            hierarchyLabelOnClick={hierarchyLabelOnClick}
            expandedNodes={expandedNodes}
            setExpandedNodes={setExpandedNodes}
            ignoreOption={ignoreOption}
            defaultIsExpanded={defaultIsExpanded}
            getNodeSeparator={getNodeSeparator}
            renderNodeLabel={renderNodeLabel}
          />
        ))}
      </div>
    );
  }
  return (
    <>
      <div
        data-has-children={hasChildren}
        data-expanded={isExpanded}
        data-disabled={Boolean(node.disabled)}
        data-selected={valueIdArray.includes(node.id as string)}
        css={styles.hierarchyNode(node)}
      >
        <div className='hierarchy-header'>
          {renderIcon()}
          <div
            className='hierarchy-header-label'
            onClick={(event) => {
              hierarchyLabelOnClick(node, event);
            }}
          >
            {renderNodeLabel ? renderNodeLabel(node) : node.label}
          </div>
        </div>
        <div className='hierarchy-children'>
          {isExpanded &&
            node.children?.map((child, i) => (
              <LightHierarchyNode
                key={i}
                valueIdArray={valueIdArray}
                getNodeIcon={getNodeIcon}
                node={child}
                styles={styles}
                hierarchyLabelOnClick={hierarchyLabelOnClick}
                expandedNodes={expandedNodes}
                setExpandedNodes={setExpandedNodes}
                ignoreOption={ignoreOption}
                defaultIsExpanded={defaultIsExpanded}
                renderNodeLabel={renderNodeLabel}
              />
            ))}
        </div>
      </div>
      {getNodeSeparator && getNodeSeparator({node, hasChildren, expanded: isExpanded, disabled, selected})}
    </>
  );
};

export const LightHierarchyFilter = <T,>(props: HierarchyFilterProps<T>) => {
  const {config, value, options, multi, onChange, hierarchyNodeCss, ignoreOption, defaultIsExpanded = false, onOpen, onClose, ...extraProps} = props;
  const intl = useIntl();
  const {normalizedOptions} = getSelectOptions(intl, props as SelectParams);
  const nodes: HierarchyNode<NormalizedOption>[] = useMemo(() => {
    return ArrayUtils.makeHierarchy(normalizedOptions, (v) => v as {id: string | number; parentId: string | number});
  }, [options]); // eslint-disable-line
  const [expandedNodes, setExpandedNodes] = useState({} as Record<string | number, boolean | undefined>);
  const [open, setOpen] = useState(false);
  // console.log('hier', {config, options, normalizedOptions, nodes});

  const theme = useTheme();
  const styles = getStyles({theme, params: {hierarchyNodeCss}});

  const valueIdArray = useMemo(() => {
    return ArrayUtils.asArray(value).map((v) => String(v));
  }, [value]);
  const hierarchyLabelOnClick = useCallback(
    (node: HierarchyNode<NormalizedOption>, event: React.MouseEvent) => {
      // console.log('hierarchyLabelOnClick', node);
      if (!onChange) return;
      // plain click selects and closes the list, CTRL/meta + click selects and keeps it open (same as the flat options list)
      if (!(multi && (event.ctrlKey || event.metaKey))) {
        setOpen(false);
        setExpandedNodes({});
      }
      if (!multi) {
        onChange(node.id);
        return;
      }

      const index = valueIdArray.indexOf(node.id as string);
      const newValue = [...(value ?? [])];
      if (index === -1) {
        newValue.push(node.id);
      } else {
        newValue.splice(index, 1);
      }
      onChange(newValue);
    },
    [onChange, multi, valueIdArray, value]
  );

  const [textInput, setTextInput] = useState('');
  const showHierarchy = textInput === '';
  const listboxProps: HierarchyListboxProps = {
    styles,
    nodes,
    valueIdArray,
    hierarchyLabelOnClick,
    expandedNodes,
    setExpandedNodes,
    ignoreOption,
    defaultIsExpanded,
    getNodeIcon: props.getNodeIcon,
    getNodeSeparator: props.getNodeSeparator,
    renderNodeLabel: props.renderNodeLabel,
  };
  // NOTE: nodes are filtered by <LightHierarchyNode>
  const filteredOptions: NormalizedOption[] = useMemo(
    () => (ignoreOption ? normalizedOptions.filter((option) => !ignoreOption(option)) : normalizedOptions),
    [normalizedOptions, ignoreOption]
  );
  return (
    <LightSelectFilter
      config={config}
      value={value}
      options={filteredOptions}
      multi={multi}
      onChange={onChange}
      open={open}
      onOpen={(event) => {
        setOpen(true);
        onOpen?.(event);
      }}
      onInputChange={(_event, value, _reason) => {
        // console.log('LightHierarchyInput.onInputChange', value, _reason);
        setTextInput(value);
      }}
      onClose={(event, reason) => {
        // console.log('LightHierarchyInput.onClose', reason);
        setOpen(false);
        setExpandedNodes({});
        // the input text is kept while CTRL-selecting from the filtered list; a closed
        // filter starts over at the hierarchy view on the next open
        setTextInput('');
        onClose?.(event, reason);
      }}
      ListboxComponent={showHierarchy ? (HierarchyListbox as unknown as JSXElementConstructor<HTMLAttributes<HTMLElement>>) : undefined}
      ListboxProps={showHierarchy ? listboxProps : undefined}
      {...extraProps}
    />
  );
};

const getStyles = (options: GetStylesOptions) => {
  const {hierarchyNodeCss} = options?.params ?? {};
  return {
    hierarchyListbox: css`
      border-radius: 16px;
      padding: 12px !important;
    `,
    hierarchyNode: (node: HierarchyNode<NormalizedOption>) => css`
      position: relative;

      & > .hierarchy-header {
        display: flex;
        align-items: center;
        column-gap: 4px;

        & > .hierarchy-expand-button {
          margin-top: 1px;
          padding: 0;
          color: var(--dlHierarchyNodeTickColor);
          transform: rotate(0deg);
          transition: all 200ms ease-in-out;
          & svg {
            width: 16px;
            height: 16px;
          }
        }
        & > .hierarchy-header-label {
          margin-bottom: 0;
          padding: 1px 0 3px 4px;
          flex: 1;
          font-size: 14px;
          line-height: 16px;
          color: #121619;
          word-break: break-word;
          user-select: none;
        }
      }
      &[data-has-children='true'] {
        & > .hierarchy-header > .hierarchy-expand-button:hover {
          color: var(--dlHierarchyNodeTickColorFocus);
        }
      }
      &[data-expanded='true'] {
        & > .hierarchy-header > .hierarchy-expand-button {
          color: var(--dlHierarchyNodeTickColorFocus);
          transform: rotate(90deg);
          transition: all 200ms ease-in-out;
        }
      }
      &[data-disabled='false'] {
        & > .hierarchy-header > .hierarchy-header-label {
          cursor: pointer;

          &:hover {
            background: #e8eaee;
          }
        }
      }
      &[data-disabled='true'][data-has-children='true'] {
        & > .hierarchy-header > .hierarchy-header-label {
          pointer-events: none;
        }
        & > .hierarchy-header > .hierarchy-expand-button {
          pointer-events: auto;
        }
      }
      &[data-selected='true'] {
        & > .hierarchy-header > .hierarchy-header-label {
          background: var(--dlTheme-500);
          color: white;

          &:hover {
            background: var(--dlTheme-600);
          }
        }
      }
      &[data-disabled='true'] {
        & > .hierarchy-header > .hierarchy-header-label {
          color: var(--dlThemeGray-500);
          pointer-events: none;
        }
      }

      & > .hierarchy-children {
        padding-left: 16px;
        display: flex;
        flex-direction: column;
        /* NOTE: we could animate height via 'transition' and 'interpolate-size: allow-keywords', hovewer, it will be rendered off-screen, so we can't actually do it */
      }
      &[data-has-children='true'][data-expanded='true'] {
        &::before {
          content: '';
          position: absolute;
          top: 16px;
          bottom: 1px;
          left: 7.5px;
          width: 1px;
          background: #ccc;
        }
      }
      ${hierarchyNodeCss ? hierarchyNodeCss(node) : undefined}
    `,
  };
};
